import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  XCircleIcon,
  ClipboardDocumentListIcon,
  PrinterIcon,
  TrashIcon,
  CubeIcon,
  CurrencyDollarIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline'
import { purchaseService } from '../../services/purchaseService'
import toast from 'react-hot-toast'

interface PurchaseItem {
  item_id: number
  product_id: number
  quantity: number
  received_quantity: number
  unit_price: number
  unit_price_base: number
  discount_percent: number
  tax_percent: number
  line_total: number
  line_total_base: number
  shipping_cost_allocated: number
  landed_cost: number
  landed_cost_base: number
  suggested_retail_price: number
  suggested_wholesale_price: number
  margin_percent: number
  products: {
    product_id: number
    product_name: string
    sku: string
    brands?: { brand_name: string }
    product_images?: { image_url: string }[]
  }
  purchase_item_serials?: { serial_number: string }[]
}

interface Purchase {
  purchase_id: number
  purchase_number: string
  purchase_date: string
  expected_delivery_date: string | null
  received_date: string | null
  status: 'draft' | 'ordered' | 'partial' | 'received' | 'cancelled'
  total_amount: number
  total_amount_base: number
  subtotal: number
  shipping_cost: number
  other_costs: number
  currency_code: string
  exchange_rate: number
  notes: string | null
  suppliers: {
    supplier_id: number
    company_name: string
    contact_person: string | null
    phone: string | null
    email: string | null
    currencies?: {
      currency_code: string
      symbol: string
    }
  } | null
  users: {
    first_name: string
    last_name: string
  } | null
  purchase_items: PurchaseItem[]
}

export const PurchaseDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'items' | 'pricing' | 'serials'>('items')
  const [marginPercent, setMarginPercent] = useState(30)
  const [showPriceCalculator, setShowPriceCalculator] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => purchaseService.getById(id!),
  })

  const deleteMutation = useMutation({
    mutationFn: () => purchaseService.delete(id!),
    onSuccess: () => {
      toast.success('Purchase cancelled successfully')
      navigate('/purchases')
    },
    onError: () => {
      toast.error('Failed to cancel purchase')
    },
  })

  const calculatePricesMutation = useMutation({
    mutationFn: (margin: number) => purchaseService.calculatePrices(id!, { margin_percent: margin, margin_type: 'retail' }),
    onSuccess: () => {
      toast.success('Prices calculated successfully')
      setShowPriceCalculator(false)
    },
    onError: () => {
      toast.error('Failed to calculate prices')
    },
  })

  const purchase: Purchase = data?.data?.data

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received': return 'bg-green-100 text-green-800'
      case 'ordered': return 'bg-blue-100 text-blue-800'
      case 'partial': return 'bg-yellow-100 text-yellow-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'received': return CheckCircleIcon
      case 'ordered': return TruckIcon
      case 'partial': return ClockIcon
      case 'cancelled': return XCircleIcon
      default: return ClipboardDocumentListIcon
    }
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to cancel this purchase?')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!purchase) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Purchase not found</p>
        <Link to="/purchases" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to purchases
        </Link>
      </div>
    )
  }

  const StatusIcon = getStatusIcon(purchase.status)
  const canReceive = purchase.status === 'draft' || purchase.status === 'ordered' || purchase.status === 'partial'
  const canCancel = purchase.status !== 'received' && purchase.status !== 'cancelled'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/purchases" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{purchase.purchase_number}</h1>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(purchase.status)}`}>
                <StatusIcon className="w-3 h-3" />
                {purchase.status}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Created by {purchase.users?.first_name} {purchase.users?.last_name} on {new Date(purchase.purchase_date).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <PrinterIcon className="w-4 h-4" />
            Print
          </button>
          {canReceive && (
            <Link
              to={`/purchases/${id}/receive`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <CheckCircleIcon className="w-4 h-4" />
              Receive Items
            </Link>
          )}
          {canCancel && (
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              <TrashIcon className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Purchase Info */}
        <div className="space-y-6">
          {/* Supplier Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Supplier</h3>
            {purchase.suppliers ? (
              <div className="space-y-2">
                <p className="font-medium text-gray-900">{purchase.suppliers.company_name}</p>
                {purchase.suppliers.contact_person && (
                  <p className="text-sm text-gray-600">{purchase.suppliers.contact_person}</p>
                )}
                {purchase.suppliers.phone && (
                  <p className="text-sm text-gray-600">{purchase.suppliers.phone}</p>
                )}
                {purchase.suppliers.email && (
                  <p className="text-sm text-gray-600">{purchase.suppliers.email}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No supplier information</p>
            )}
          </div>

          {/* Dates */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Dates</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Order Date</span>
                <span className="text-sm font-medium">{new Date(purchase.purchase_date).toLocaleDateString()}</span>
              </div>
              {purchase.expected_delivery_date && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Expected Delivery</span>
                  <span className="text-sm font-medium">{new Date(purchase.expected_delivery_date).toLocaleDateString()}</span>
                </div>
              )}
              {purchase.received_date && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Received Date</span>
                  <span className="text-sm font-medium">{new Date(purchase.received_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Financial Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Subtotal</span>
                <span className="text-sm font-medium">{purchase.currency_code} {Number(purchase.subtotal).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Shipping</span>
                <span className="text-sm font-medium">{purchase.currency_code} {Number(purchase.shipping_cost).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Other Costs</span>
                <span className="text-sm font-medium">{purchase.currency_code} {Number(purchase.other_costs).toLocaleString()}</span>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900">Total</span>
                  <div className="text-right">
                    <p className="font-bold text-lg">{purchase.currency_code} {Number(purchase.total_amount).toLocaleString()}</p>
                    {purchase.currency_code !== 'KES' && (
                      <p className="text-xs text-gray-500">KSh {Number(purchase.total_amount_base).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {purchase.notes && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
              <p className="text-sm text-gray-600">{purchase.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow border border-gray-100">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {[
                  { id: 'items', label: 'Items', icon: CubeIcon },
                  { id: 'pricing', label: 'Pricing', icon: CurrencyDollarIcon },
                  { id: 'serials', label: 'Serial Numbers', icon: QrCodeIcon },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'items' && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {purchase.purchase_items.map((item) => (
                          <tr key={item.item_id}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                  {item.products.product_images?.[0] ? (
                                    <img
                                      src={item.products.product_images[0].image_url}
                                      alt={item.products.product_name}
                                      className="w-full h-full object-cover rounded-lg"
                                    />
                                  ) : (
                                    <CubeIcon className="w-5 h-5 text-gray-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{item.products.product_name}</p>
                                  <p className="text-xs text-gray-500">{item.products.sku}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`${item.received_quantity >= item.quantity ? 'text-green-600' : 'text-yellow-600'}`}>
                                {item.received_quantity}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <p>{purchase.currency_code} {Number(item.unit_price).toLocaleString()}</p>
                              {purchase.currency_code !== 'KES' && (
                                <p className="text-xs text-gray-500">KSh {Number(item.unit_price_base).toLocaleString()}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">
                              <p>{purchase.currency_code} {Number(item.line_total).toLocaleString()}</p>
                              {purchase.currency_code !== 'KES' && (
                                <p className="text-xs text-gray-500">KSh {Number(item.line_total_base).toLocaleString()}</p>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'pricing' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Suggested Selling Prices</h3>
                    <button
                      onClick={() => setShowPriceCalculator(!showPriceCalculator)}
                      className="text-sm text-primary-600 hover:underline"
                    >
                      {showPriceCalculator ? 'Hide Calculator' : 'Price Calculator'}
                    </button>
                  </div>

                  {showPriceCalculator && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Margin Percentage
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={marginPercent}
                          onChange={(e) => setMarginPercent(Number(e.target.value))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <button
                          onClick={() => calculatePricesMutation.mutate(marginPercent)}
                          disabled={calculatePricesMutation.isPending}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                          Calculate
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Landed Cost</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margin %</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retail Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wholesale Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {purchase.purchase_items.map((item) => (
                          <tr key={item.item_id}>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-gray-900">{item.products.product_name}</p>
                              <p className="text-xs text-gray-500">{item.products.sku}</p>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              KSh {Number(item.landed_cost_base).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">{item.margin_percent}%</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600">
                              KSh {Number(item.suggested_retail_price).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-blue-600">
                              KSh {Number(item.suggested_wholesale_price).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'serials' && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Received Serial Numbers</h3>
                  {purchase.purchase_items.every(item => !item.purchase_item_serials?.length) ? (
                    <div className="text-center py-8 text-gray-500">
                      <QrCodeIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No serial numbers received yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {purchase.purchase_items.map((item) => (
                        item.purchase_item_serials && item.purchase_item_serials.length > 0 && (
                          <div key={item.item_id} className="border rounded-lg p-4">
                            <p className="font-medium text-gray-900 mb-2">{item.products.product_name}</p>
                            <div className="flex flex-wrap gap-2">
                              {item.purchase_item_serials?.map((serial, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded"
                                >
                                  {serial.serial_number}
                                </span>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
